import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import BottomNav from "../../components/BottomNav";
import axios from "../../utils/axiosInstance";
import { format } from "date-fns";

function Card({ title, value, sub }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4">
      <div className="text-xs text-slate-500 dark:text-slate-400">{title}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{sub}</div> : null}
    </div>
  );
}

function Bars({ title, data }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4">
      <div className="text-sm font-semibold text-slate-900 dark:text-white">{title}</div>
      <div className="mt-3 space-y-2">
        {data.map((d) => (
          <div key={d.label} className="flex items-center gap-3">
            <div className="w-20 text-xs text-slate-500 dark:text-slate-400 truncate">{d.label}</div>
            <div className="flex-1 h-2 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.round((d.value / max) * 100)}%`,
                  background: "currentColor",
                }}
              />
            </div>
            <div className="w-12 text-right text-xs text-slate-500 dark:text-slate-400">{d.value}</div>
          </div>
        ))}
      </div>
      <style>{`.dark div[style*="currentColor"]{color:#e2e8f0} div[style*="currentColor"]{color:#0f172a}`}</style>
    </div>
  );
}

const num = (v) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) => num(v).toFixed(2);

const safeDate = (d) => {
  const x = d ? new Date(d) : null;
  return x && !Number.isNaN(x.getTime()) ? x : null;
};

function monthKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export default function UserStatsPage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [txs, setTxs] = useState([]); 
  const [walletTxs, setWalletTxs] = useState([]); 
  const [walletTransfers, setWalletTransfers] = useState([]); 

  useEffect(() => {
    (async () => {
      try {
        setErr("");
        setLoading(true);

        const [a, b, c] = await Promise.all([
          axios.get(`/api/transactions/mine?page=1&pageSize=500`),
          axios.get(`/api/wallet/transactions?limit=300`),
          axios.get(`/api/wallet/transfers?limit=300`),
        ]);

        setTxs(a.data?.transactions || []);

        const wt =
          b.data?.transactions ||
          b.data?.walletTransactions ||
          b.data?.rows ||
          (Array.isArray(b.data) ? b.data : []);
        setWalletTxs(wt);

        const wtr =
          c.data?.transfers ||
          c.data?.walletTransfers ||
          c.data?.rows ||
          (Array.isArray(c.data) ? c.data : []);
        setWalletTransfers(wtr);
      } catch (e) {
        console.error("Stats load error:", e);
        setErr("Impossible de charger vos statistiques (transactions / wallet).");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activity = useMemo(() => {
    const list = [];

    for (const t of txs || []) {
      const d = safeDate(t.createdAt);
      list.push({
        kind: "transfer_tx",
        id: `tx:${t.id}`,
        createdAt: d,
        status: String(t.status || "unknown").toLowerCase(),
        title: `Transfert — ${t.recipientName || "-"}`,
        amount: num(t.amount ?? t.amountToSend),
        currency: t.currency || "",
        fee: num(t.fee),
        note: t.note || "",
        meta: t,
      });
    }

    for (const w of walletTxs || []) {
      const d = safeDate(w.createdAt);
      const t = String(w.type || "wallet").toLowerCase();
      const status = String(w.status || "unknown").toLowerCase();

      list.push({
        kind: "wallet_tx",
        id: `wtx:${w.id || w.reference || Math.random()}`,
        createdAt: d,
        status,
        title: t === "fund" ? "Wallet — Dépôt" : t === "withdraw" ? "Wallet — Retrait" : `Wallet — ${t}`,
        amount: num(w.amount),
        currency: w.currency || w.feeCurrency || "",
        fee: num(w.feeAmount),
        note: w.note || "",
        meta: w,
      });
    }

    for (const tr of walletTransfers || []) {
      const d = safeDate(tr.createdAt);
      const status = String(tr.status || "unknown").toLowerCase();

      list.push({
        kind: "wallet_transfer",
        id: `wtr:${tr.id || tr.reference || Math.random()}`,
        createdAt: d,
        status,
        title: `Transfert Wallet — ${tr.recipientName || tr.recipientPhone || "-"}`,
        amount: num(tr.amount),
        currency: tr.feeCurrency || "XAF", 
        fee: num(tr.feeAmount),
        note: tr.note || "",
        meta: tr,
      });
    }

    return list
      .filter((x) => x.createdAt) 
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [txs, walletTxs, walletTransfers]);

  const stats = useMemo(() => {
    const totalActivities = activity.length;

    const byKind = {};
    const byStatus = {};
    const monthCounts = new Map();

    let totalFees = 0;

    let walletIn = 0;
    let walletOut = 0;

    let walletTransferOut = 0;

    let totalTransferSent = 0;

    for (const a of activity) {
      byKind[a.kind] = (byKind[a.kind] || 0) + 1;
      byStatus[a.status] = (byStatus[a.status] || 0) + 1;
      totalFees += num(a.fee);

      const mk = monthKey(a.createdAt);
      monthCounts.set(mk, (monthCounts.get(mk) || 0) + 1);

      if (a.kind === "transfer_tx") {
        totalTransferSent += num(a.amount);
      }

      if (a.kind === "wallet_tx") {
        const t = String(a.meta?.type || "").toLowerCase();
        // adjust if your wallet tx uses "deposit/withdraw" etc.
        if (t === "fund" || t === "deposit" || t === "credit") walletIn += num(a.amount);
        if (t === "withdraw" || t === "debit") walletOut += num(a.amount);
      }

      if (a.kind === "wallet_transfer") {
        walletTransferOut += num(a.amount);
      }
    }

    const monthKeys = Array.from(monthCounts.keys()).sort().slice(-8);
    const monthBars = monthKeys.map((k) => ({ label: k.slice(5), value: monthCounts.get(k) || 0 }));

    const kindBars = Object.entries(byKind)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value }));

    const statusBars = Object.entries(byStatus)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));

    const completed = byStatus["completed"] || 0;
    const completionRate = totalActivities ? Math.round((completed / totalActivities) * 100) : 0;

    return {
      totalActivities,
      totalFees,
      completionRate,
      totalTransferSent,
      walletIn,
      walletOut,
      walletTransferOut,
      monthBars: monthBars.length ? monthBars : [{ label: "—", value: 0 }],
      kindBars: kindBars.length ? kindBars : [{ label: "—", value: 0 }],
      statusBars: statusBars.length ? statusBars : [{ label: "—", value: 0 }],
      recent: activity.slice(0, 20),
    };
  }, [activity]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <Sidebar />
        <main className="flex-1 pb-28 md:pb-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Statistiques
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Toutes vos activités : transactions, wallet, transferts wallet.
              </p>
            </div>

            {loading ? (
              <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-6">
                <p className="text-slate-500 dark:text-slate-400">Chargement…</p>
              </div>
            ) : err ? (
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-sm text-rose-700 dark:text-rose-300">
                {err}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card title="Activités (total)" value={stats.totalActivities} sub="Transactions + wallet + transferts wallet" />
                  <Card title="Taux complété" value={`${stats.completionRate}%`} />
                  <Card title="Total frais (toutes activités)" value={fmt(stats.totalFees)} />
                  <Card title="Transferts envoyés (transactions)" value={fmt(stats.totalTransferSent)} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Card title="Wallet — Entrées (fund)" value={fmt(stats.walletIn)} />
                  <Card title="Wallet — Sorties (withdraw)" value={fmt(stats.walletOut)} />
                  <Card title="Transferts Wallet — Sorties" value={fmt(stats.walletTransferOut)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="lg:col-span-2">
                    <Bars title="Activité par mois (nb. opérations)" data={stats.monthBars} />
                  </div>
                  <div className="space-y-3">
                    <Bars title="Répartition par type" data={stats.kindBars} />
                    <Bars title="Répartition par statut" data={stats.statusBars} />
                  </div>
                </div>

                <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-black/5 dark:border-white/10">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Activité récente</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Dernières 20 opérations</div>
                  </div>

                  <div className="divide-y divide-black/5 dark:divide-white/10">
                    {stats.recent.length === 0 ? (
                      <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Aucune activité.</div>
                    ) : (
                      stats.recent.map((a) => (
                        <div key={a.id} className="p-4 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                              {a.title}
                            </div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {format(a.createdAt, "dd/MM/yyyy HH:mm")} • {a.kind} • {a.status}
                            </div>
                            {a.note ? (
                              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 truncate">
                                Note: {a.note}
                              </div>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-slate-900 dark:text-white">
                              {fmt(a.amount)} {a.currency}
                            </div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Frais: {fmt(a.fee)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="md:hidden fixed bottom-0 left-0 w-full z-50">
            <BottomNav />
          </div>
        </main>
      </div>
    </div>
  );
}
